import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import {
  generatorDutyClassSchema,
  generatorRedundancyModeSchema,
  generatorSizingStatusSchema,
  generatorSizingWarningCodeSchema,
  REQUIRED_GENERATOR_CHOICES,
} from "./generatorSizingContract";
import { ApiError, describeApiFieldError } from "./http";
import { loadScenarioSchema } from "./loadDemandContract";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

// A factor left out means NOT ESTABLISHED, never 1: the engine sizes with 1,
// names the factor in a warning and reports REVIEW_REQUIRED (Master Prompt
// A16 (a)). The form must omit a blank field instead of sending "1".
const optionalFactor = exactDecimalSchema.optional();

/** The same sentence the Transformer rating schedule uses. */
const RATINGS_ASCENDING = "Ratings must be unique and in ascending order.";

export const generatorSizingRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    steady_state_demand_kw: exactDecimalSchema,
    // An engineering input, not a margin: it has no default and is required.
    steady_state_power_factor: exactDecimalSchema,
    available_unit_ratings_kva: z.array(exactDecimalSchema).min(1),
    // A step load left out is NOT ESTABLISHED (sized as 0, REVIEW_REQUIRED); an
    // entered "0" states there is none and is sent as "0" (A17).
    transient_step_load_kva: optionalFactor,
    transient_allowance_factor: optionalFactor,
    future_growth_factor: optionalFactor,
    design_margin_factor: optionalFactor,
    ambient_derating_factor: optionalFactor,
    altitude_derating_factor: optionalFactor,
    duty_units: z.number().int().positive().optional(),
    standby_units: z.number().int().nonnegative().optional(),
    // The engineer's choice; the backend imposes no default (A17). Optional here
    // only so a missing choice is reported by the rule below as a missing value.
    duty_class: generatorDutyClassSchema.optional(),
    redundancy_mode: generatorRedundancyModeSchema.optional(),
    scenario: loadScenarioSchema.optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
    // The GENERATOR source of the revision's diagram this study sizes (A28 (d)); left out when
    // none is chosen, so a study that does not use the link is sent as before.
    sld_element_code: z.string().trim().min(1).max(50).optional(),
  })
  .strict()
  .superRefine((values, ctx) => {
    // Mirrors the backend rule (schemas/generator_sizing.py): the schedule
    // must be unique and ascending. Only the first offending entry is flagged,
    // so the message points at one place on screen; an entry that is blank or
    // not a plain decimal already carries its own issue.
    let previous: number | null = null;
    for (const [index, raw] of values.available_unit_ratings_kva.entries()) {
      if (!exactDecimalSchema.safeParse(raw).success) {
        continue;
      }
      const current = Number(raw);
      if (previous !== null && !(current > previous)) {
        ctx.addIssue({
          code: "custom",
          message: RATINGS_ASCENDING,
          path: ["available_unit_ratings_kva", index],
        });
        break;
      }
      previous = current;
    }

    for (const field of REQUIRED_GENERATOR_CHOICES) {
      if (values[field] === undefined) {
        // Reported as a missing value rather than an unknown option, so the form's
        // describeValidationIssue turns it into "... is required." - the same way
        // the Transformer form reports a missing demand power factor.
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
  });

export const generatorSizingWarningSchema = z
  .object({
    code: generatorSizingWarningCodeSchema,
    message: z.string(),
  })
  .strict();

export const generatorSizingResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    scenario: loadScenarioSchema,
    duty_class: generatorDutyClassSchema,
    redundancy_mode: generatorRedundancyModeSchema,
    steady_state_demand_kw: z.string(),
    steady_state_power_factor: z.string(),
    steady_state_demand_kva: z.string(),
    // The factor the sizing used; "1" when it was not established.
    future_growth_factor: z.string(),
    future_steady_state_kva: z.string(),
    design_margin_factor: z.string(),
    steady_state_required_kva: z.string(),
    // The step load the sizing used; "0" when it was not established.
    transient_step_load_kva: z.string(),
    transient_allowance_factor: z.string(),
    transient_additional_kva: z.string(),
    transient_required_kva: z.string(),
    governing_required_kva: z.string(),
    combined_derating_factor: z.string(),
    required_nameplate_capacity_kva: z.string(),
    duty_units: z.number().int(),
    standby_units: z.number().int(),
    total_units: z.number().int(),
    required_unit_rating_kva: z.string(),
    // Null together with the four below when no rating was adequate (NO_SOLUTION).
    selected_unit_rating_kva: z.string().nullable(),
    installed_nameplate_capacity_kva: z.string().nullable(),
    derated_duty_capacity_kva: z.string().nullable(),
    spare_derated_capacity_kva: z.string().nullable(),
    steady_state_loading_percent: z.string().nullable(),
    status: generatorSizingStatusSchema,
    warnings: z.array(generatorSizingWarningSchema),
    jurisdiction_profile: jurisdictionProfileSchema,
  })
  .strict();

export const generatorRunCreateRequestSchema = z
  .object({
    study: generatorSizingRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const generatorRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: generatorSizingResponseSchema,
  })
  .strict();

export type GeneratorSizingRequest = z.infer<typeof generatorSizingRequestSchema>;
export type GeneratorSizingWarning = z.infer<typeof generatorSizingWarningSchema>;
export type GeneratorSizingResponse = z.infer<typeof generatorSizingResponseSchema>;
export type GeneratorRunCreateRequest = z.infer<typeof generatorRunCreateRequestSchema>;
export type GeneratorRunResponse = z.infer<typeof generatorRunResponseSchema>;

function formatApiError(data: unknown, status: number): string {
  return describeApiFieldError(data, status, "Generator sizing failed");
}

/**
 * Calculate a generator (DG) sizing study and persist it as a run revision.
 *
 * The request carries the whole run body, because a study's notes belong to the
 * run, not to the sizing the engine calculates. The returned summary carries the
 * run ID, engine version, profile, reference status and content hash shown in
 * the traceability panel.
 *
 * With a project revision id the run is stored under that revision of the selected
 * project; without one it belongs to no project. An unchanged study returns the
 * stored revision with HTTP 200 instead of a new one (A11); both are valid here.
 */
export async function createGeneratorRun(
  payload: GeneratorRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<GeneratorRunResponse> {
  const validatedPayload = generatorRunCreateRequestSchema.parse(payload);
  const timeoutSignal = AbortSignal.timeout(30_000);
  const requestSignal = signal
    ? AbortSignal.any([signal, timeoutSignal])
    : timeoutSignal;

  const response = await fetch("/api/v1/electrical/generator-sizing/runs", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal,
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status), response.status);
  }

  const parsed = generatorRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS generator runs API.");
  }

  return parsed.data;
}
